import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoLocationOutline, IoHeartOutline, IoChatbubbleEllipsesOutline, IoPersonAddOutline, IoCheckmarkOutline, IoCloseOutline, IoTimeOutline, IoDocumentTextOutline, IoPeopleOutline, IoEllipsisHorizontal } from 'react-icons/io5';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/userApi';
import { friendshipApi } from '../services/friendshipApi';
import { postApi } from '../services/postApi';
import Avatar from '../components/Avatar';
import PostCard from '../components/PostCard';
import UserRelationshipCard from '../components/UserRelationshipCard';
import { useChat } from '../contexts/ChatContext';
import './Profile.css';

// Simple Dropdown for Profile Header
const ProfileDropdown = ({ onUnfriend }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="profile-action-dropdown" ref={dropdownRef}>
      <button
        className="profile-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More actions"
      >
        <IoEllipsisHorizontal />
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          <button
            className="dropdown-item danger"
            onClick={() => {
              setIsOpen(false);
              onUnfriend?.();
            }}
          >
            <IoCloseOutline />
            <span>{t('friends.unfriend') || 'Hủy kết bạn'}</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => setIsOpen(false)}
          >
            <span>{t('friends.block') || 'Chặn'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Confirm Dialog Component
const ConfirmDialog = ({ isOpen, title, message, confirmText, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="confirm-dialog-overlay" onClick={onCancel}>
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="confirm-dialog-title">{title}</h3>
        <p className="confirm-dialog-message">{message}</p>
        <div className="confirm-dialog-actions">
          <button className="confirm-dialog-btn cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="confirm-dialog-btn confirm" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, updateUser } = useAuth();
  const { createConversation, setActiveConversationId } = useChat();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [friends, setFriends] = useState([]);
  const [friendships, setFriendships] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showUnfriendDialog, setShowUnfriendDialog] = useState(false);

  // Get user ID from username
  const id = user?.id?.toString() || username;

  const isOwnProfile = currentUser?.id === user?.id;

  // Tab navigation
  const activeTab = location.pathname.includes('/friends') ? 'friends' : 'posts';

  useEffect(() => {
    loadUser();
  }, [username]);

  // Load posts when user is loaded
  useEffect(() => {
    if (user?.id) {
      loadPosts();
    }
  }, [user?.id]);

  useEffect(() => {
    loadFriends();
    loadFriendships();
  }, [currentUser?.id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      // If username is a number, treat as ID
      const userId = parseInt(username);
      let response;
      if (!isNaN(userId)) {
        response = await userApi.getUser(userId);
      } else {
        response = await userApi.getUserByUsername(username);
      }

      if (response.success) {
        setUser(response.data);
        setFormData({
          display_name: response.data.display_name,
          bio: response.data.bio || '',
          marital_status: response.data.marital_status || '',
          hobbies: response.data.hobbies || [],
          living_place: response.data.living_place || '',
          language: response.data.language,
        });
      }
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFriends = async () => {
    try {
      const response = await friendshipApi.getFriends();
      if (response.success) {
        // Filter out current user and profile user from friends list
        const allFriends = response.data || [];
        const filteredFriends = allFriends.filter(
          friend => friend.id !== currentUser?.id && friend.id !== user?.id
        );
        setFriends(filteredFriends);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    }
  };

  // Calculate mutual friends between current user and profile user
  const getMutualFriends = (friendId) => {
    // This would need the profile user's friends list
    // For now, we'll show a simplified indicator
    return 0;
  };

  const loadFriendships = async () => {
    try {
      const response = await friendshipApi.getFriendships();
      if (response.success) {
        setFriendships(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load friendships:', error);
    }
  };

  // Get friendship status with any user
  const getFriendStatus = (friendId) => {
    if (!friendships || friendships.length === 0 || !friendId) return null;

    // Check if already friends
    const friendship = friendships.find(
      f => f.status === 'accepted' &&
           (f.requester_id === friendId || f.addressee_id === friendId)
    );
    if (friendship) return { status: 'friends', friendship };

    // Check if they sent me a request (I'm the addressee)
    const pendingToMe = friendships.find(
      f => f.status === 'pending' && f.addressee_id === currentUser?.id && f.requester_id === friendId
    );
    if (pendingToMe) return { status: 'pending_to_me', friendship: pendingToMe };

    // Check if I sent them a request (I'm the requester)
    const pendingFromMe = friendships.find(
      f => f.status === 'pending' && f.requester_id === currentUser?.id && f.addressee_id === friendId
    );
    if (pendingFromMe) return { status: 'pending_from_me', friendship: pendingFromMe };

    return null;
  };

  // Get target user ID for friendship actions
  const targetUserId = user?.id;

  // Determine friendship status with this user
  const getFriendshipStatus = () => {
    if (!friendships || friendships.length === 0 || !targetUserId) return null;

    // Check if already friends
    const friendship = friendships.find(
      f => f.status === 'accepted' &&
           (f.requester_id === targetUserId || f.addressee_id === targetUserId)
    );
    if (friendship) return { status: 'friends', friendship };

    // Check if they sent me a request (I'm the addressee)
    const pendingToMe = friendships.find(
      f => f.status === 'pending' && f.addressee_id === currentUser?.id && f.requester_id === targetUserId
    );
    if (pendingToMe) return { status: 'pending_to_me', friendship: pendingToMe };

    // Check if I sent them a request (I'm the requester)
    const pendingFromMe = friendships.find(
      f => f.status === 'pending' && f.requester_id === currentUser?.id && f.addressee_id === targetUserId
    );
    if (pendingFromMe) return { status: 'pending_from_me', friendship: pendingFromMe };

    return null;
  };

  const friendshipStatus = !isOwnProfile ? getFriendshipStatus() : null;

  const handleAddFriend = async () => {
    if (!targetUserId) return;
    setActionLoading(true);
    try {
      await friendshipApi.sendFriendRequest(targetUserId);
      loadFriendships();
    } catch (error) {
      console.error('Failed to send friend request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    setActionLoading(true);
    try {
      await friendshipApi.acceptFriendRequest(friendshipStatus.friendship.id);
      loadFriendships();
      loadFriends();
    } catch (error) {
      console.error('Failed to accept request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    setActionLoading(true);
    try {
      await friendshipApi.deleteFriendship(friendshipStatus.friendship.id);
      loadFriendships();
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfriend = async () => {
    setActionLoading(true);
    try {
      await friendshipApi.deleteFriendship(friendshipStatus.friendship.id);
      loadFriendships();
      loadFriends();
      setShowUnfriendDialog(false);
    } catch (error) {
      console.error('Failed to unfriend:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    setActionLoading(true);
    try {
      await friendshipApi.deleteFriendship(friendshipStatus.friendship.id);
      loadFriendships();
    } catch (error) {
      console.error('Failed to cancel request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const loadPosts = async () => {
    if (!user?.id) return;
    try {
      setLoadingPosts(true);
      const response = await postApi.getUserPosts(user.id, 20, 0);
      if (response.success) {
        setPosts(response.data || []);

        // Batch load comments for all posts
        const postIds = (response.data || []).map(p => p.id).filter(id => id);
        if (postIds.length > 0) {
          try {
            const commentsResponse = await postApi.getBatchComments(postIds);
            if (commentsResponse.data) {
              setPostComments(commentsResponse.data);
            }
          } catch (err) {
            console.error('Error loading batch comments:', err);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(post =>
      post.id === updatedPost.id ? { ...post, ...updatedPost } : post
    ));
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      display_name: user.display_name,
      bio: user.bio || '',
      marital_status: user.marital_status || '',
      hobbies: user.hobbies || [],
      living_place: user.living_place || '',
      language: user.language,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    try {
      const response = await userApi.updateUser(user.id, formData);
      if (response.success) {
        setUser(response.data);
        updateUser(response.data);
        setEditing(false);
      }
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!targetUserId) return;
    try {
      const conv = await createConversation([targetUserId], false);
      setActiveConversationId(conv.id);
      navigate('/chat');
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user?.id) return;

    try {
      const response = await userApi.uploadAvatar(user.id, file);
      if (response.success) {
        loadUser();
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    }
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  if (!user) {
    return <div>User not found</div>;
  }

  return (
    <div className="profile-container fade-in">
      <div className="profile-card">
        <div className="profile-header">
          <div className="avatar-container">
            <Avatar user={user} size="xlarge" />
            {isOwnProfile && (
              <label className="avatar-upload-btn">
                <input type="file" accept="image/*" onChange={handleAvatarUpload} />
                {t('profile.uploadAvatar')}
              </label>
            )}
          </div>

          <div className="profile-identity">
            <h1>{user.display_name}</h1>
            <p className="username">@{user.username}</p>
          </div>

          {isOwnProfile && !editing && (
            <button className="profile-edit-btn" onClick={handleEdit}>{t('profile.editProfile')}</button>
          )}
          {!isOwnProfile && friendshipStatus?.status === 'friends' && (
            <div className="profile-actions-with-dropdown">
              <button className="profile-message-btn" onClick={handleSendMessage}>
                <IoChatbubbleEllipsesOutline />
                {t('chat.sendMessage')}
              </button>
              <ProfileDropdown onUnfriend={() => setShowUnfriendDialog(true)} />
            </div>
          )}
          {!isOwnProfile && friendshipStatus?.status === 'pending_to_me' && (
            <div className="friend-request-actions">
              <button className="profile-accept-btn" onClick={handleAcceptRequest} disabled={actionLoading}>
                <IoCheckmarkOutline />
                {t('friends.accept')}
              </button>
              <button className="profile-reject-btn" onClick={handleRejectRequest} disabled={actionLoading}>
                <IoCloseOutline />
                {t('friends.reject')}
              </button>
            </div>
          )}
          {!isOwnProfile && friendshipStatus?.status === 'pending_from_me' && (
            <button className="profile-cancel-btn" onClick={handleCancelRequest} disabled={actionLoading}>
              <IoTimeOutline />
              {t('friends.cancelRequest')}
            </button>
          )}
          {!isOwnProfile && !friendshipStatus && (
            <button className="profile-add-friend-btn" onClick={handleAddFriend} disabled={actionLoading}>
              <IoPersonAddOutline />
              {t('friends.addFriend')}
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSave} className="profile-form">
            <div className="form-group">
              <label>{t('profile.displayName')}</label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('profile.bio')}</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>{t('profile.maritalStatus')}</label>
              <input
                type="text"
                name="marital_status"
                value={formData.marital_status}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>{t('profile.livingPlace')}</label>
              <input
                type="text"
                name="living_place"
                value={formData.living_place}
                onChange={handleChange}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="primary">{t('common.save')}</button>
              <button type="button" onClick={handleCancel}>{t('common.cancel')}</button>
            </div>
          </form>
        ) : (
          <>
            {user.bio && (
              <p className="profile-bio">{typeof user.bio === 'string' ? user.bio : user.bio.String}</p>
            )}

            <div className="profile-meta">
              {user.living_place && (
                <div className="profile-meta-item">
                  <IoLocationOutline />
                  <span>{typeof user.living_place === 'string' ? user.living_place : user.living_place.String}</span>
                </div>
              )}
              {user.marital_status && (
                <div className="profile-meta-item">
                  <IoHeartOutline />
                  <span>{typeof user.marital_status === 'string' ? user.marital_status : user.marital_status.String}</span>
                </div>
              )}
            </div>

            <div className="profile-stats">
              {friends.length > 0 && (
                <button
                  className="profile-stat-clickable"
                  onClick={() => navigate(`/profile/${user.username}/friends`)}
                  aria-label={`View all ${friends.length} friends`}
                >
                  <strong>{friends.length}</strong> {t('friends.friends') || 'Friends'}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Profile Tabs */}
      <div className="profile-tabs">
        <Link
          to={`/profile/${user.username}`}
          className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
          aria-label="View posts"
        >
          <IoDocumentTextOutline />
          {t('post.posts') || 'Posts'}
        </Link>
        <Link
          to={`/profile/${user.username}/friends`}
          className={`profile-tab ${activeTab === 'friends' ? 'active' : ''}`}
          aria-label="View friends"
        >
          <IoPeopleOutline />
          {t('friends.friends') || 'Friends'}
          {friends.length > 0 && <span className="tab-count">{friends.length}</span>}
        </Link>
      </div>

      {activeTab === 'posts' && (
      <div className="profile-posts-section">
        <h2>{t('post.posts') || 'Posts'}</h2>
        {loadingPosts ? (
          <div className="posts-skeleton">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton-post-card">
                <div className="skeleton-header">
                  <div className="skeleton-avatar"></div>
                  <div className="skeleton-user-info">
                    <div className="skeleton-name"></div>
                    <div className="skeleton-time"></div>
                  </div>
                </div>
                <div className="skeleton-content">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
                <div className="skeleton-actions">
                  <div className="skeleton-action"></div>
                  <div className="skeleton-action"></div>
                  <div className="skeleton-action"></div>
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="no-posts">
            <div className="empty-icon">
              <IoDocumentTextOutline />
            </div>
            <p className="empty-title">
              {isOwnProfile
                ? (t('post.noPosts') || 'No posts yet')
                : (t('post.userNoPosts') || 'No posts yet')}
            </p>
            <p className="empty-desc">
              {isOwnProfile
                ? (t('post.noPostsDesc') || 'Share something with your friends!')
                : (t('post.userNoPostsDesc') || 'This user has not posted anything.')}
            </p>
          </div>
        ) : (
          <div className="posts-list">
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={currentUser?.id}
                initialComments={postComments[post.id]}
                onPostDeleted={handlePostDeleted}
                onPostUpdated={handlePostUpdated}
                onPostShared={() => loadPosts()}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {activeTab === 'friends' && (
        <div className="friends-section">
          {friends.length > 0 ? (
            <>
              <div className="friends-list-unified">
                {friends.slice(0, 6).map((friend) => {
                  const friendStatus = getFriendStatus(friend.id);
                  return (
                    <UserRelationshipCard
                      key={friend.id}
                      user={friend}
                      relationship={friendStatus?.status === 'friends' ? 'friends' : 'none'}
                      onAddFriend={async () => {
                        try {
                          await friendshipApi.sendFriendRequest(friend.id);
                          loadFriendships();
                        } catch (error) {
                          console.error('Failed to send friend request:', error);
                        }
                      }}
                      onUnfriend={async () => {
                        try {
                          await friendshipApi.deleteFriendship(friendStatus.friendship.id);
                          loadFriendships();
                        } catch (error) {
                          console.error('Failed to unfriend:', error);
                        }
                      }}
                      onMessage={async () => {
                        try {
                          const conv = await createConversation([friend.id], false);
                          setActiveConversationId(conv.id);
                          navigate('/chat');
                        } catch (error) {
                          console.error('Failed to start conversation:', error);
                        }
                      }}
                    />
                  );
                })}
              </div>
              {friends.length > 6 && (
                <button
                  className="view-all-friends-btn"
                  onClick={() => navigate(`/profile/${user.username}/friends`)}
                >
                  {t('friends.viewAllFriends') || 'View all friends'} ({friends.length})
                </button>
              )}
            </>
          ) : (
            <div className="no-friends">
              <div className="no-friends-icon">
                <IoPeopleOutline />
              </div>
              <p className="no-friends-title">
                {isOwnProfile
                  ? (t('profile.noFriendsYet') || "Chưa có bạn bè")
                  : (t('profile.noFriends') || 'Chưa có bạn bè')}
              </p>
              <p className="no-friends-desc">
                {isOwnProfile
                  ? (t('profile.noFriendsYetDesc') || 'Hãy kết bạn với người dùng khác để hiển thị ở đây.')
                  : (t('profile.noFriendsDesc') || 'Người dùng này chưa kết bạn với ai.')}
              </p>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={showUnfriendDialog}
        title={t('friends.unfriendConfirmTitle') || 'Unfriend this user?'}
        message={t('friends.unfriendConfirmMessage') || 'They will be removed from your friends list.'}
        confirmText={t('friends.unfriend') || 'Unfriend'}
        onConfirm={handleUnfriend}
        onCancel={() => setShowUnfriendDialog(false)}
      />
    </div>
  );
};

export default Profile;
